/**
 * Smart TV Detection & Performance Utilities
 * Detects Smart TVs, low-power devices, and provides optimization flags
 */

export interface DeviceCapabilities {
  isSmartTV: boolean;
  isLowPowerDevice: boolean;
  supportsHover: boolean;
  memoryLimitMB: number;
  shouldUseLightMode: boolean;
}

/**
 * Detect if the current device is a Smart TV
 */
export const detectSmartTV = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  
  // Exclude desktop and mobile browsers first (most important check)
  const isDesktopOrMobile = /windows nt|mac os x|linux|android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  
  // If it's clearly a desktop/mobile browser, it's NOT a TV
  if (isDesktopOrMobile) {
    // But check if it's explicitly a TV user agent (some TVs report as Android)
    const explicitTVIdentifiers = [
      'googletv',
      'appletv',
      'smarttv',
      'smart-tv',
      'hbbtv',
      'netcast',
      'nettv',
      'webos',
      'tizen',
      'roku',
    ];
    
    const hasExplicitTVIdentifier = explicitTVIdentifiers.some(id => userAgent.includes(id));
    if (!hasExplicitTVIdentifier) {
      return false; // It's a regular computer/phone, not a TV
    }
  }
  
  // Common Smart TV user agents (for devices that don't report as desktop/mobile)
  const tvIdentifiers = [
    'tv',
    'smarttv',
    'smart-tv',
    'googletv',
    'appletv',
    'hbbtv',
    'pov_tv',
    'netcast',
    'nettv',
    'philips',
    'panasonic',
    'sony',
    'tizen',
    'webos',
    'crkey', // Chromecast
    'viera',
    'bravia',
    'roku',
    'dlnadoc',
    'ce-html',
  ];

  // Check user agent
  const isTVUserAgent = tvIdentifiers.some(identifier => 
    userAgent.includes(identifier)
  );

  return isTVUserAgent;
};

/**
 * Detect if device is low-power (weak CPU/RAM)
 * Only flags devices that are BOTH low-spec AND likely embedded/TV devices
 * Regular mobile phones are NOT considered low-power
 */
export const detectLowPowerDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  
  // Mobile devices are fine - they can handle our features
  const isMobile = /mobile|android|iphone|ipad|ipod/.test(userAgent);
  if (isMobile) return false;

  // Only flag as low-power if it's a TV/embedded device with very limited specs
  const deviceMemory = (navigator as any).deviceMemory;
  const cores = navigator.hardwareConcurrency;
  
  // Only flag if device has BOTH very low memory AND very low CPU
  const hasVeryLowSpecs = 
    (deviceMemory && deviceMemory <= 2) && // 2GB or less
    (cores && cores <= 2); // 2 or fewer cores

  return hasVeryLowSpecs;
};

/**
 * Check if device supports hover interactions
 */
export const supportsHover = (): boolean => {
  if (typeof window === 'undefined') return true;
  
  // Check media query
  const hoverQuery = window.matchMedia('(hover: hover)');
  return hoverQuery.matches;
};

/**
 * Estimate available memory limit
 */
export const estimateMemoryLimit = (): number => {
  const deviceMemory = (navigator as any).deviceMemory;
  
  if (deviceMemory) {
    return deviceMemory * 1024; // Convert GB to MB
  }

  // Smart TVs typically have 512MB - 2GB
  if (detectSmartTV()) {
    return 512;
  }

  // Default estimate for unknown devices
  return 2048;
};

/**
 * Get comprehensive device capabilities
 */
export const getDeviceCapabilities = (): DeviceCapabilities => {
  const isSmartTV = detectSmartTV();
  const isLowPowerDevice = detectLowPowerDevice();
  const memoryLimitMB = estimateMemoryLimit();
  
  // Only enable light mode for actual Smart TVs
  // Regular mobile devices should work normally
  const shouldUseLightMode = isSmartTV;

  return {
    isSmartTV,
    isLowPowerDevice,
    supportsHover: supportsHover(),
    memoryLimitMB,
    shouldUseLightMode,
  };
};

/**
 * Store device capabilities in localStorage for persistence
 */
export const cacheDeviceCapabilities = (capabilities: DeviceCapabilities): void => {
  try {
    localStorage.setItem('device_capabilities', JSON.stringify(capabilities));
  } catch (error) {
    console.warn('Failed to cache device capabilities:', error);
  }
};

/**
 * Load cached device capabilities
 */
export const getCachedDeviceCapabilities = (): DeviceCapabilities | null => {
  try {
    const cached = localStorage.getItem('device_capabilities');
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Allow user to manually override light mode
 */
export const setLightModeOverride = (enabled: boolean): void => {
  try {
    localStorage.setItem('light_mode_override', enabled ? 'true' : 'false');
    // Also clear device capabilities cache so detection runs fresh on next load
    localStorage.removeItem('device_capabilities');
  } catch (error) {
    console.warn('Failed to set light mode override:', error);
  }
};

/**
 * Check if user has manually overridden light mode
 */
export const getLightModeOverride = (): boolean | null => {
  try {
    const override = localStorage.getItem('light_mode_override');
    if (override === 'true') return true;
    if (override === 'false') return false;
    return null;
  } catch (error) {
    return null;
  }
};
